import { PaletteOptions } from '@mui/material';

import type { ThemeName } from './tokens';

// Literal values per theme, mirrored from theme/tokens.css: MUI computes alpha/darken from these, so they can't be
// CSS variables. Overrides that only paint should use `tx` (theme/tokens.ts) instead.
const SURFACES: Record<
  ThemeName,
  {
    mode: 'light' | 'dark';
    text: string;
    muted: string;
    faint: string;
    onText: string;
    line: string;
    fill: string;
    fill2: string;
    ground: string;
    paper: string;
    solid: string;
    selected: string;
  }
> = {
  light: {
    mode: 'light',
    text: '#373736',
    muted: '#6E6E6C',
    faint: '#8A8986',
    onText: '#F7F7F6',
    line: 'rgba(55, 55, 54, 0.09)',
    fill: 'rgba(55, 55, 54, 0.06)',
    fill2: 'rgba(55, 55, 54, 0.1)',
    ground: '#E8E8E8',
    paper: '#FFFFFF',
    solid: '#FFFFFF',
    selected: '#E8E8E8',
  },
  natural: {
    mode: 'light',
    text: '#3E4248',
    muted: '#6E6A65',
    faint: '#8A8580',
    onText: '#F2F0EB',
    line: 'rgba(62, 66, 72, 0.1)',
    fill: 'rgba(110, 106, 101, 0.09)',
    fill2: 'rgba(110, 106, 101, 0.14)',
    // Hauser's ground is chalk in both modes (theme/hauser.css)
    ground: '#BBB7AF',
    paper: '#E8E7E2',
    solid: '#F2F0EB',
    selected: '#D3CFC6',
  },
  dark: {
    mode: 'dark',
    text: '#F7F7F6',
    muted: '#A4A3A0',
    faint: '#84837F',
    onText: '#262626',
    line: 'rgba(247, 247, 246, 0.07)',
    fill: 'rgba(247, 247, 246, 0.05)',
    fill2: 'rgba(247, 247, 246, 0.08)',
    ground: '#BBB7AF',
    paper: '#303030',
    solid: '#424242',
    selected: 'rgba(247, 247, 246, 0.12)',
  },
};

export const createAppPalette = (themeName: ThemeName): PaletteOptions => {
  const s = SURFACES[themeName];
  return {
    mode: s.mode,
    // Brand family: lime is the one action colour, concrete chalk the brand neutral
    primary: {
      main: '#C0FF02',
      light: '#CFFF3F',
      dark: '#A6DC00',
      contrastText: '#1F1F1F',
    },
    secondary: {
      main: '#BBB7AF',
      contrastText: '#262626',
    },
    // Hauser's own status palette, unchanged
    error: {
      main: '#B05670',
      contrastText: '#FFFFFF',
    },
    error2: {
      main: '#E47192',
      contrastText: '#000000',
    },
    warning: {
      main: '#E68A6B',
      contrastText: '#000000',
    },
    warning2: {
      main: '#EBC064',
      contrastText: '#000000',
    },
    success: {
      main: '#B5D274',
      contrastText: '#000000',
    },
    success2: {
      main: '#C0FF02',
      contrastText: '#000000',
    },
    info: {
      main: '#6BB1B1',
      contrastText: '#000000',
    },
    text: {
      primary: s.text,
      secondary: s.muted,
      secondary2: s.faint,
      tertiary: s.onText,
      disabled: s.faint,
    },
    divider: s.line,
    background: {
      default: s.ground,
      defaultLight: s.solid,
      defaultLighter: s.solid,
      paper: s.paper,
      neutral: s.fill,
    },
    action: {
      hover: s.fill,
      selected: s.selected,
      disabledOpacity: 0.4,
    },
  };
};
